import { useMutation, useQuery } from "@vue/apollo-composable"
import { defineStore } from "pinia"
import { computed, watch } from "vue"

import {
  type ConfigurationSchema as GqlConfigurationSchema,
  type Configuration,
  type CustomerFilter,
  type ConfigurationLevel,
  type MutationRootWriteConfigArgs,
  type OrganizationFilter,
  FunctionalRole,
} from "@/graphql/types"
import ConfigurationByContextQuery from "@/graphql/configuration/ConfigurationByContext.gql"
import ConfigurationSchemaQuery from "@/graphql/configuration/ConfigurationSchema.gql"
import RawCustomerConfigurationQuery from "@/graphql/configuration/RawCustomerConfiguration.gql"
import RawInstitutionConfigurationQuery from "@/graphql/configuration/RawInstitutionConfiguration.gql"
import RawOrganizationConfigurationQuery from "@/graphql/configuration/RawOrganizationConfiguration.gql"
import RawSystemConfigurationQuery from "@/graphql/configuration/RawSystemConfiguration.gql"
import UpdateConfigurationMutation from "@/graphql/configuration/UpdateConfiguration.gql"
import { flatten } from "@/utils/kibig/utils"

import { useSessionStore } from "./session"

export type ConfigurationSchema = Omit<
  ConfigurationSchemaQueryResult["configurationSchema"],
  "__typename"
>

export type MappedConfiguration = {
  [K in keyof ConfigurationSchema]?: string | number | boolean
} & {
  level: ConfigurationLevel
}

export type ConfigurationKey = keyof ConfigurationSchema

export type ConfigurationQueryResult = { configuration: Configuration }
type ConfigurationMutationResult = { writeConfig: boolean }
type RawSystemConfigurationQueryResult = { rawSystemConfiguration?: Configuration }
type RawCustomerConfigurationQueryResult = { rawCustomerConfiguration?: Configuration }
type RawOrganizationConfigurationQueryResult = { rawOrganizationConfiguration?: Configuration }
type RawInstitutionConfigurationQueryResult = { rawInstitutionConfiguration?: Configuration }
type ConfigurationSchemaQueryResult = { configurationSchema: GqlConfigurationSchema }

export const useConfigurationStore = defineStore("configuration", () => {
  const sessionStore = useSessionStore()

  const context = computed(() => ({
    ...(sessionStore.institutionFilter.customer
      ? { customer: sessionStore.institutionFilter.customer }
      : null),
    ...(sessionStore.institutionFilter.organization
      ? { organization: sessionStore.institutionFilter.organization }
      : null),
    ...(sessionStore.institutionFilter.institution
      ? { institution: sessionStore.institutionFilter.institution }
      : null),
  }))
  const editorQueriesEnabled = computed(() => sessionStore.hasRole(FunctionalRole.Configuration))

  const { result: configurationQueryResult, loading: configurationQueryLoading } =
    useQuery<ConfigurationQueryResult>(
      ConfigurationByContextQuery,
      computed(() => ({
        context: context.value,
      }))
    )

  const { result: schemaQueryResult, loading: configurationSchemaLoading } =
    useQuery<ConfigurationSchemaQueryResult>(
      ConfigurationSchemaQuery,
      {},
      computed(() => ({ enabled: editorQueriesEnabled }))
    )

  const { result: systemConfigurationQueryResult } = useQuery<RawSystemConfigurationQueryResult>(
    RawSystemConfigurationQuery,
    {},
    computed(() => ({
      enabled: editorQueriesEnabled,
    }))
  )

  const { result: customerConfigurationQueryResult } =
    useQuery<RawCustomerConfigurationQueryResult>(
      RawCustomerConfigurationQuery,
      computed<{ context: CustomerFilter }>(() => ({
        context: {
          customer: context.value.customer,
        },
      })),
      computed(() => ({
        enabled: !!context.value.customer && editorQueriesEnabled,
      }))
    )

  const { result: organizationConfigurationQueryResult } =
    useQuery<RawOrganizationConfigurationQueryResult>(
      RawOrganizationConfigurationQuery,
      computed<{ context: OrganizationFilter }>(() => ({
        context: { customer: context.value.customer, organization: context.value.organization },
      })),
      computed(() => ({
        enabled: !!context.value.organization && editorQueriesEnabled,
      }))
    )

  const { result: institutionConfigurationQueryResult } =
    useQuery<RawInstitutionConfigurationQueryResult>(
      RawInstitutionConfigurationQuery,
      computed(() => ({
        context: context.value,
      })),
      computed(() => ({
        enabled: !!context.value.institution && editorQueriesEnabled,
      }))
    )

  watch(
    () => context.value,
    (ctx) => {
      if (!ctx.institution) institutionConfigurationQueryResult.value = undefined
      if (!ctx.organization) organizationConfigurationQueryResult.value = undefined
      if (!ctx.customer) customerConfigurationQueryResult.value = undefined
    },
    { flush: "pre" }
  )

  const { mutate: writeConfiguration, loading: writeConfigurationLoading } = useMutation<
    ConfigurationMutationResult,
    MutationRootWriteConfigArgs
  >(UpdateConfigurationMutation, {
    errorPolicy: "all",
    awaitRefetchQueries: true,
    refetchQueries: [
      "ConfigurationByContext",
      "RawSystemConfiguration",
      "RawCustomerConfiguration",
      "RawOrganizationConfiguration",
      "RawInstitutionConfiguration",
    ],
  })

  const configuration = computed<MappedConfiguration | undefined>(() =>
    flatten(configurationQueryResult.value?.configuration)
  )

  const configurationSchema = computed<ConfigurationSchema | undefined>(() => {
    if (!schemaQueryResult.value) return undefined
    const { __typename: _, ...schema } = schemaQueryResult.value.configurationSchema
    return schema
  })

  const loading = computed(() => configurationQueryLoading)
  return {
    configurationQueryResult,
    configuration,
    loading,
    configurationSchema,
    configurationSchemaLoading,
    rawSystemConfiguration: computed<MappedConfiguration | undefined>(() =>
      flatten(systemConfigurationQueryResult.value?.rawSystemConfiguration)
    ),
    rawCustomerConfiguration: computed<MappedConfiguration | undefined>(() =>
      flatten(customerConfigurationQueryResult.value?.rawCustomerConfiguration)
    ),
    rawOrganizationConfiguration: computed<MappedConfiguration | undefined>(() =>
      flatten(organizationConfigurationQueryResult.value?.rawOrganizationConfiguration)
    ),
    rawInstitutionConfiguration: computed<MappedConfiguration | undefined>(() =>
      flatten(institutionConfigurationQueryResult.value?.rawInstitutionConfiguration)
    ),
    writeConfiguration,
    writeConfigurationLoading,
    editorQueriesEnabled,
  }
})
